import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Trash2, Sparkles } from "lucide-react";
import { Toast } from "../../components/Toast";

type Format = "lowercase" | "uppercase";

function uuidv4Fallback(): string {
  // RFC4122 version 4 UUID via crypto.getRandomValues
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // Set version to 4
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  // Set variant to RFC4122
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
}

function generateUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return uuidv4Fallback();
}

export function UUIDGenerator() {
  const { t } = useTranslation();

  const [count, setCount] = useState<number>(5);
  const [format, setFormat] = useState<Format>("lowercase");
  const [withBraces, setWithBraces] = useState(false);
  const [output, setOutput] = useState("");
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const normalizedCount = useMemo(() => {
    const n = Number.isFinite(count) ? count : 1;
    return Math.min(100, Math.max(1, Math.trunc(n)));
  }, [count]);

  const formatUuid = useCallback(
    (uuid: string) => {
      const formatted = format === "uppercase" ? uuid.toUpperCase() : uuid.toLowerCase();
      return withBraces ? `{${formatted}}` : formatted;
    },
    [format, withBraces]
  );

  const handleGenerate = useCallback(() => {
    const uuids = Array.from({ length: normalizedCount }, () => formatUuid(generateUuid()));
    setOutput(uuids.join("\n"));
  }, [normalizedCount, formatUuid]);

  const handleCopyAll = useCallback(async () => {
    if (!output.trim()) return;
    try {
      await navigator.clipboard.writeText(output);
      setToastMessage(t("uuidGenerator.copied"));
    } catch {
      setToastMessage(t("uuidGenerator.copyFailed"));
    }
  }, [output, t]);

  const handleClear = useCallback(() => {
    setOutput("");
  }, []);

  return (
    <div className="uuid-generator">
      <div className="uuid-controls">
        <div className="uuid-field">
          <label className="uuid-label" htmlFor="uuid-count">
            {t("uuidGenerator.count")}
          </label>
          <div className="uuid-count-row">
            <input
              id="uuid-count"
              className="uuid-input"
              inputMode="numeric"
              type="number"
              min={1}
              max={100}
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              placeholder={t("uuidGenerator.countHint")}
            />
          </div>
        </div>

        <div className="uuid-field">
          <label className="uuid-label" htmlFor="uuid-format">
            {t("uuidGenerator.format")}
          </label>
          <select
            id="uuid-format"
            className="uuid-select"
            value={format}
            onChange={(e) => setFormat(e.target.value as Format)}
          >
            <option value="lowercase">{t("uuidGenerator.lowercase")}</option>
            <option value="uppercase">{t("uuidGenerator.uppercase")}</option>
          </select>
        </div>

        <label className="uuid-checkbox">
          <input
            type="checkbox"
            checked={withBraces}
            onChange={(e) => setWithBraces(e.target.checked)}
          />
          <span>{t("uuidGenerator.braces")}</span>
        </label>
      </div>

      <div className="uuid-actions">
        <button type="button" className="uuid-action-btn primary" onClick={handleGenerate}>
          <Sparkles size={16} />
          {t("uuidGenerator.generate")}
        </button>
        <button
          type="button"
          className="uuid-action-btn"
          onClick={handleCopyAll}
          disabled={!output.trim()}
        >
          <Copy size={16} />
          {t("uuidGenerator.copyAll")}
        </button>
        <button
          type="button"
          className="uuid-action-btn"
          onClick={handleClear}
          disabled={!output.trim()}
        >
          <Trash2 size={16} />
          {t("uuidGenerator.clear")}
        </button>
      </div>

      <div className="uuid-field">
        <label className="uuid-label" htmlFor="uuid-output">
          {t("uuidGenerator.outputLabel")}
        </label>
        <textarea
          id="uuid-output"
          className="uuid-textarea"
          value={output}
          onChange={(e) => setOutput(e.target.value)}
          placeholder={t("uuidGenerator.outputPlaceholder")}
          spellCheck={false}
        />
      </div>

      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage(null)} />}
    </div>
  );
}

